import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import {
    MetricDto,
    AggregateOperationDtoAggregateFunctionEnum,
    DataSourceDto,
} from 'apps/marsinet/src/app/typescript-axios';

import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { ModalAddMetricProps } from 'apps/marsinet/src/app/models/metricModels';
import { useAddMetricValidationSchema } from 'apps/marsinet/src/app/validations/addMetricValidationSchema';
import { canSubmit, getFormErrorMessage, handleNextStep, handlePreviousStep, isFormFieldInvalid, onPageHide, Step, stepHasChanges, stepIsValid } from '../../utils/form_utils';
import { FormStepFooter } from '../../utils/forms-step-footer';
import { getAvailableAggregateFunction, getPostGroupingColumnOptions } from '../../utils/datasource_utils';


const INITIAL_VALUES = {
    metric: {
        name: '',
        data_source_name: '',
        aggregate_operation: {
            aggregate_function: undefined as unknown as AggregateOperationDtoAggregateFunctionEnum,
            column: {
                table: '',
                column: '',
                type: undefined,
            },
        },
    } as MetricDto,
};

const STEPS: Step[] = [
    {
        label: 'pages.dashboards.metric.selectDataSource',
        fields: ['metric.data_source_name'],
        required: true,
    },
    {
        label: 'pages.dashboards.metric.selectAggregation',
        fields: ['metric.aggregate_operation.aggregate_function', 'metric.aggregate_operation.column'],
        required: true,
    },
    {
        label: 'pages.dashboards.metric.name',
        fields: ['metric.name'],
        required: true,
    },
];

const ModalAddMetric: React.FC<ModalAddMetricProps> = ({
    visible,
    setVisible,
    availableDataSources,
    createMetricFunction,
}) => {
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);

    const formik = useFormik({
        initialValues: INITIAL_VALUES,
        validationSchema: useAddMetricValidationSchema(availableDataSources.map((ds: DataSourceDto) => ds.name)),
        validateOnMount: false,
        onSubmit: async (values, { resetForm, setFieldError, setFieldTouched, setStatus }) => {
            setStatus(null);
            try {
                await createMetricFunction(values);

                resetForm();
                setActiveStep(0);
                setVisible(false);
            } catch (error) {
                if (error instanceof Error && error.message.includes('already exists')) {
                    setFieldError('metric.name', error.message);
                    setFieldTouched('metric.name', true, false);
                }
                else {
                    console.error('Error creating metric:', error);
                    formik.setStatus({ submitError: 'Failed to create metric. Please try again.' });
                }
            }
        },
    });

    const isCurrentStepRequired = STEPS[activeStep].required;

    const isNextDisabled = isCurrentStepRequired
        ? !stepHasChanges(STEPS[activeStep].fields, formik.values, formik.initialValues) || !stepIsValid(STEPS[activeStep].fields, formik.errors)
        : false;

    const isSkipping = !isCurrentStepRequired && !stepHasChanges(STEPS[activeStep].fields, formik.values, formik.initialValues);

    const isLastStep = activeStep === STEPS.length - 1;


    const dataSourceOptions = availableDataSources.map((ds) => ({
        label: ds.name,
        value: ds.name,
    }));

    const selectedDataSource = availableDataSources.find(
        (ds) => ds.name === formik.values.metric.data_source_name
    );

    const columnOptions = getPostGroupingColumnOptions(undefined, undefined, (selectedDataSource?.query_definition?.columns ?? []), selectedDataSource?.query_definition?.group_by);

    const selectedAlias = Object.entries(columnOptions).find(
        ([, dto]) =>
            dto.table === formik.values.metric.aggregate_operation.column?.table &&
            dto.column === formik.values.metric.aggregate_operation.column?.column
    )?.[0] ?? null;

    const availableAggregateFunctions: AggregateOperationDtoAggregateFunctionEnum[] =
        formik.values.metric.aggregate_operation.column.type
            ? getAvailableAggregateFunction(formik.values.metric.aggregate_operation.column.type)
            : Object.values(AggregateOperationDtoAggregateFunctionEnum);

    // ── Footer ───────────────────────────────────────────────────────────────

    const metric_form_name: string = "metric-form";

    const footer = <FormStepFooter
        activeStep={activeStep}
        isLastStep={isLastStep}
        isSkipping={isSkipping}
        isNextDisabled={isNextDisabled}
        canSubmit={canSubmit(formik.initialValues, formik.values, formik.isValid)}
        onPrevious={() => handlePreviousStep(activeStep, STEPS, setActiveStep, formik)}
        onNext={() => handleNextStep(activeStep, STEPS, isSkipping, setActiveStep, formik)}
        labels={{
            back: t('pages.dashboards.back'),
            next: t('pages.dashboards.next'),
            skip: t('pages.dashboards.skip'),
            create: t('pages.dashboards.createMetric'),
        }}
        form_name={metric_form_name}
    />;

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Dialog
            header={t('pages.dashboards.createMetric')}
            visible={visible}
            className="mx-2 md:w-6 lg:w-4"
            onHide={() => onPageHide(formik, setActiveStep, setVisible)}
            breakpoints={{ '960px': '75vw', '641px': '100vw' }}
            blockScroll={true}
            footer={footer}
        >
            {/* Step label */}
            <div className="mb-3 text-lg font-semibold text-900">
                {t(STEPS[activeStep].label)}
            </div>

            {/* Step progress dots */}
            <div className="flex gap-2 mb-4">
                {STEPS.map((_, i) => (
                    <span
                        key={i}
                        className={`border-round-xl transition-all transition-duration-150 ${i === activeStep
                            ? 'bg-primary'
                            : i < activeStep
                                ? 'bg-primary-200'
                                : 'bg-300'
                            }`}
                        style={{ height: '6px', flex: 1 }}
                    />
                ))}
            </div>

            <form id={metric_form_name} autoComplete="off" onSubmit={formik.handleSubmit}>
                <div className="grid formgrid p-fluid">


                    {/* ── Step 1: Data Source ── */}
                    {activeStep === 0 && (
                        <div className="field col-12">
                            <Dropdown
                                id="metric-datasource"
                                name="metric.data_source_name"
                                value={formik.values.metric.data_source_name}
                                options={dataSourceOptions}
                                onChange={async (e) => {
                                    await formik.setFieldValue('metric.data_source_name', e.value);
                                    formik.setFieldTouched('metric.data_source_name', true, true);
                                    await formik.setFieldValue('metric.aggregate_operation.column', { table: '', column: '', type: undefined });
                                    await formik.setFieldValue('metric.aggregate_operation.aggregate_function', undefined);
                                }}
                                className={isFormFieldInvalid('metric.data_source_name', formik.touched, formik.errors) ? 'p-invalid' : ''}
                                style={{ width: '100%' }}
                                optionLabel="label"
                                filter
                                emptyMessage="No data sources available"
                            />
                            {isFormFieldInvalid('metric.data_source_name', formik.touched, formik.errors) && (
                                <small className="p-error block mt-1">
                                    {getFormErrorMessage('metric.data_source_name', formik.touched, formik.errors)}
                                </small>
                            )}
                        </div>
                    )}

                    {/* ── Step 2: Aggregate Operation ── */}
                    {activeStep === 1 && (
                        <>
                            <div className="field col-12">
                                <label htmlFor="metric-column" className="font-medium mb-2 block">
                                    {t('pages.dashboards.metric.aggregateColumn')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <Dropdown
                                    id="metric-column"
                                    value={selectedAlias}
                                    options={Object.keys(columnOptions)}
                                    onChange={async (e) => {
                                        const selectedColumn = columnOptions[e.value];
                                        await formik.setFieldValue('metric.aggregate_operation.column', selectedColumn);
                                        formik.setFieldTouched('metric.aggregate_operation.column', true, true); // 👈
                                        await formik.setFieldValue('metric.aggregate_operation.aggregate_function', undefined);
                                        formik.setFieldTouched('metric.aggregate_operation.aggregate_function', false, false);
                                    }}
                                    className={
                                        isFormFieldInvalid('metric.aggregate_operation.column.column', formik.touched, formik.errors)
                                            ? 'p-invalid'
                                            : ''
                                    }
                                    style={{ width: '100%' }}
                                    filter
                                />

                                {isFormFieldInvalid('metric.aggregate_operation.column.column', formik.touched, formik.errors) && (
                                    <small className="p-error block mt-1">
                                        {getFormErrorMessage('metric.aggregate_operation.column.column', formik.touched, formik.errors)}
                                    </small>
                                )}
                            </div>
                            {/* Aggregate function picker */}
                            <div className="field col-12">
                                <label className="font-medium mb-2 block">
                                    {t('pages.dashboards.metric.aggregateFunction')}
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <Dropdown
                                    id="metric-aggregate-function"
                                    name="metric.aggregate_operation.aggregate_function"
                                    value={formik.values.metric.aggregate_operation.aggregate_function ?? null}
                                    options={availableAggregateFunctions.map((fn) => ({
                                        label: fn.toUpperCase(),
                                        value: fn,
                                    }))}
                                    onChange={async (e) => {
                                        await formik.setFieldValue('metric.aggregate_operation.aggregate_function', e.value);
                                        formik.setFieldTouched('metric.aggregate_operation.aggregate_function', true, true); // 👈
                                    }}
                                    className={
                                        isFormFieldInvalid('metric.aggregate_operation.aggregate_function', formik.touched, formik.errors)
                                            ? 'p-invalid'
                                            : ''
                                    }
                                    style={{ width: '100%' }}
                                />
                                {isFormFieldInvalid('metric.aggregate_operation.aggregate_function', formik.touched, formik.errors) && (
                                    <small className="p-error block mt-1">
                                        {getFormErrorMessage('metric.aggregate_operation.aggregate_function', formik.touched, formik.errors)}
                                    </small>
                                )}
                            </div>

                        </>
                    )}

                    {/* ── Step 3: Name ── */}
                    {activeStep === 2 && (
                        <div className="field col-12">
                            <InputText
                                id="metric-name"
                                name="metric.name"
                                value={formik.values.metric.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                className={isFormFieldInvalid('metric.name', formik.touched, formik.errors) ? 'p-invalid' : ''}
                                style={{ width: '100%' }}
                                autoFocus
                            />
                            {isFormFieldInvalid('metric.name', formik.touched, formik.errors) && (
                                <small className="p-error block mt-1">
                                    {getFormErrorMessage('metric.name', formik.touched, formik.errors)}
                                </small>
                            )}
                        </div>
                    )}

                    {formik.status?.submitError && (
                        <div className="col-12 flex justify-content-center align-items-center text-red-500 mt-2">
                            {formik.status.submitError}
                        </div>
                    )}
                </div>
            </form>
        </Dialog>
    );
};

export default ModalAddMetric;